import { useEffect, useMemo } from "react";
import {
  ChartDatum,
  ExperienceUsageData,
  LOG_RATIO_THRESHOLD,
  MIN_DISPLAY_VALUE,
  MetricType,
  ScaleMode,
  buildLinearTicks,
  buildLogTicks,
} from "../utils/usageUtils";

interface UseUsageChartDataProps {
  data: ExperienceUsageData[];
  availableMetrics: MetricType[];
  selectedMetrics: MetricType[];
  sortByMetric: MetricType;
  scaleMode: ScaleMode;
  setScaleMode: (mode: ScaleMode) => void;
  metricColors: Record<MetricType, string>;
  isUserLevelDataEnabled?: boolean;
}

const FIXED_Y_TICKS_COUNT = 6;

export interface UseUsageChartDataResult {
  chartData: ChartDatum[];
  logJustified: boolean;
  yAxisDomainMax: number;
  yAxisTicks: number[];
  useLogScale: boolean;
}

export const useUsageChartData = ({
  data,
  availableMetrics,
  selectedMetrics,
  sortByMetric,
  scaleMode,
  setScaleMode,
  metricColors,
  isUserLevelDataEnabled = false,
}: UseUsageChartDataProps): UseUsageChartDataResult => {
  const minDisplay = isUserLevelDataEnabled ? 0 : MIN_DISPLAY_VALUE;
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const valA =
        (a[sortByMetric as keyof ExperienceUsageData] as number) || 0;
      const valB =
        (b[sortByMetric as keyof ExperienceUsageData] as number) || 0;

      return valB - valA;
    });
  }, [data, sortByMetric]);

  const scaleConfig = useMemo(() => {
    if (sortedData.length === 0 || selectedMetrics.length === 0) {
      return {
        logJustified: false,
        yAxisDomainMax: minDisplay + 1,
        yAxisTicks: [minDisplay, minDisplay + 1],
        useLogScale: false,
      };
    }

    const rawValues = sortedData.flatMap((exp) =>
      selectedMetrics.map(
        (metric) => (exp[metric as keyof ExperienceUsageData] as number) || 0,
      ),
    );

    const positives = rawValues
      .filter((v) => Number.isFinite(v) && v > 0)
      .map((v) => Math.max(v, minDisplay || 1));
    const trueDataMax = Math.max(...positives, minDisplay || 1);
    const minPositive = positives.length
      ? Math.min(...positives)
      : minDisplay || 1;

    const justified =
      trueDataMax / Math.max(minPositive, minDisplay || 1) >=
      LOG_RATIO_THRESHOLD;

    const actualUseLog = justified && scaleMode === "log";

    // Log scale requires a positive axis floor; 0 is not valid for log domains.
    // When user-level data is enabled minDisplay is 0, so use 1 as the floor
    // for log-scale ticks and domain. Bar values are clamped to the same floor
    // below, since a non-positive value on a log axis breaks the whole chart.
    const logSafeMin = actualUseLog ? Math.max(minDisplay, 1) : minDisplay;

    const exactMax = Math.max(trueDataMax, logSafeMin + 1);
    const withBuffer = exactMax * 1.1;
    const maxRoundedToTheNearest5 = Math.ceil(withBuffer / 10) * 10;

    const yAxisTicks = actualUseLog
      ? buildLogTicks(logSafeMin, maxRoundedToTheNearest5, FIXED_Y_TICKS_COUNT)
      : buildLinearTicks(
          minDisplay,
          maxRoundedToTheNearest5,
          FIXED_Y_TICKS_COUNT,
        );

    return {
      logJustified: justified,
      yAxisDomainMax: maxRoundedToTheNearest5,
      yAxisTicks,
      useLogScale: actualUseLog,
    };
  }, [sortedData, selectedMetrics, scaleMode, minDisplay]);

  useEffect(() => {
    if (!scaleConfig.logJustified && scaleMode === "log")
      setScaleMode("linear");
  }, [scaleConfig.logJustified, scaleMode, setScaleMode]);

  const chartData: ChartDatum[] = useMemo(() => {
    const logFloor = Math.max(minDisplay, 1);

    return sortedData.map((exp) => {
      const d: Record<string, any> = {
        id: exp.id,
        name: exp.name || `Experience ${exp.id.slice(0, 8)}`,
      };

      availableMetrics.forEach((metric) => {
        const rawValue =
          (exp[metric as keyof ExperienceUsageData] as number) || 0;

        d[`raw_${metric}`] = rawValue;

        const fillKey = `fill${metric.charAt(0).toUpperCase() + metric.slice(1)}`;

        d[fillKey] =
          !isUserLevelDataEnabled && rawValue < MIN_DISPLAY_VALUE
            ? `${metricColors[metric]}40`
            : metricColors[metric];

        if (selectedMetrics.includes(metric)) {
          const barValue = isUserLevelDataEnabled
            ? rawValue
            : Math.max(rawValue, MIN_DISPLAY_VALUE);

          d[metric] = scaleConfig.useLogScale
            ? Math.max(barValue, logFloor)
            : barValue;
        }
      });

      return d as ChartDatum;
    });
  }, [
    sortedData,
    selectedMetrics,
    availableMetrics,
    metricColors,
    isUserLevelDataEnabled,
    minDisplay,
    scaleConfig.useLogScale,
  ]);

  return { chartData, ...scaleConfig };
};
